import * as React from 'react';
import './pageManage.scss';
import { useActions } from '../schemaModalActions';
import { cn, dndMove, getSchema } from '../../../utils';
import { ModalGroup, ModalGroups } from '../../ModalGroup/modalGroup';
import { NodeType } from '../../../enums/NodeType';
import { Dropdown } from '../../Dropdown/dropdown';
import { ThreeDots, MoveTo, MoveToTop, MoveToBottom } from '../../Icon/icon';
import { Cancel, Primary, Buttons } from 'rcd-cpp-ui/ui/button';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { useAlert } from '../../Alert/alert';
import { useSelector } from '../../../utils/useSelector';
import { SchemaField } from '../../../types/Metadata';
import { ItemType } from '../../../enums/ItemType';
import { ItemFieldCardWrapper } from '../ItemFieldCard/itemFieldCardWrapper';
import { DataType } from '../../../enums/DataType';
import { findOne } from 'rcd-cpp-workspace/utils/array';

const NON_MOVABLE_FIELDS = [
  'title',
  'audienceDetails',
  'itemInfo',
  'contentInfo'
];

export const PageManage = () => {
  const fields = useSelector(s => s.schemaModal?.fields);
  const itemTypes = useSelector(
    s =>
      getSchema(
        s.planning?.selectedWorkspace?.schemas,
        NodeType.Item
      )?.props?.fields?.find(f => f.field == 'itemType')?.enumValues
  );

  const { updateSchemaForm } = useActions();

  const [tab, setTab] = React.useState(ItemType.content as string);

  const schemaForm = useSelector(
    s => s.schemaModal?.schemaForms.filter(sf => sf.props.itemType == tab)[0]
  );

  const updateFields = (fields: SchemaField[]) => {
    if (schemaForm) {
      const groupFields = fields
        .filter(f => f.dataType == DataType.group)
        .map(f => f.field);
      schemaForm.props.fields.forEach(f => {
        if (
          groupFields.includes(f.parentField) &&
          !findOne(fields, { field: f.field })
        ) {
          groupFields.push(f.field);
          fields.push(f);
        }
      });
      schemaForm.props.fields = fields;
      updateSchemaForm({ ...schemaForm });
    }
  };

  const fieldsToDisplay = schemaForm?.props?.fields
    ?.filter(f => !f.parentField)
    .map(f => {
      const existingField = fields.find(ef => ef.field == f.field);
      if (existingField) {
        return {
          ...f,
          ...existingField,
          requiredAt: f.requiredAt,
          showAt: f.showAt
        };
      }
      return f;
    });

  return (
    <ModalGroups id="manageFields">
      <div className="item-type-tabs workspace-setting">
        <nav>
          {Object.entries(itemTypes).map(([k, v]) => (
            <div
              key={k}
              className={cn({ active: tab == k })}
              onClick={() => setTab(k)}
            >
              <span data-txt={v}>{v}</span>
            </div>
          ))}
        </nav>
      </div>
      <ModalGroup
        label={<>Manage Fields</>}
        text={
          'Please use this tab to manage the item field settings. To add a brand new field, please use the configure tab.'
        }
      >
        <div>
          <div>
            <div>
              <div>Field Name</div>
              <div>Type</div>
              <div>Required to Enter</div>
              <div>Show starting at</div>
            </div>
            <div></div>
          </div>
        </div>
        <div className="scrollable">
          {fieldsToDisplay?.map((f, index) => {
            const disableDnd = f.field === 'title';

            return (
              <ItemFieldCardWrapper
                key={f.field}
                field={f}
                index={index}
                schemaForm={schemaForm}
                onMove={
                  disableDnd
                    ? undefined
                    : dndMove(
                        fieldsToDisplay,
                        updateFields,
                        null,
                        schemaForm.props.fields
                      )
                }
              >
                <FieldActionsMenu
                  index={index}
                  fieldsToDisplay={fieldsToDisplay}
                  updateFields={updateFields}
                  schemaFormFields={schemaForm?.props?.fields}
                  disableDnd={disableDnd}
                />
              </ItemFieldCardWrapper>
            );
          })}
        </div>
      </ModalGroup>
    </ModalGroups>
  );
};

const FieldActionsMenu = ({
  index,
  fieldsToDisplay,
  updateFields,
  schemaFormFields,
  disableDnd
}) => {
  enum Action {
    MoveTo = 'Move To...',
    MoveToTop = 'Move To Top',
    MoveToBottom = 'Move To Bottom'
  }
  const moveField = dndMove(
    fieldsToDisplay,
    updateFields,
    null,
    schemaFormFields
  );
  const [showMoveModal, setShowMoveModal] = React.useState(false);

  const minMovableIndex = fieldsToDisplay.reduce((maxIndex, field, idx) => {
    return NON_MOVABLE_FIELDS.includes(field.field) ? idx + 1 : maxIndex;
  }, 0);

  const handleSelect = (action: Action) => {
    switch (action) {
      case Action.MoveTo:
        setShowMoveModal(true);
        return;
      case Action.MoveToTop:
        moveField(index, minMovableIndex);
        return;
      case Action.MoveToBottom:
        moveField(index, fieldsToDisplay.length);
        return;
    }
  };

  return (
    <div className="field-actions-menu-container">
      <Dropdown
        position="right"
        className="field-actions-menu"
        options={[Action.MoveTo, Action.MoveToTop, Action.MoveToBottom]}
        disabled={action => {
          if (disableDnd) return true;
          if (action == Action.MoveToTop && index == minMovableIndex)
            return true;
          if (
            action == Action.MoveToBottom &&
            index == fieldsToDisplay.length - 1
          )
            return true;
          if (action == Action.MoveTo && fieldsToDisplay.length < 2)
            return true;
        }}
        onSelect={handleSelect}
        renderIcon={action => {
          switch (action) {
            case Action.MoveTo:
              return <MoveTo />;
            case Action.MoveToTop:
              return <MoveToTop />;
            case Action.MoveToBottom:
              return <MoveToBottom />;
          }
        }}
      >
        <div className="three-dots">
          <ThreeDots />
        </div>
      </Dropdown>
      {showMoveModal && (
        <MoveModal
          setShowMoveModal={setShowMoveModal}
          index={index}
          fieldsToDisplay={fieldsToDisplay}
          updateFields={updateFields}
          minMovableIndex={minMovableIndex}
        />
      )}
    </div>
  );
};

const MoveModal = ({
  setShowMoveModal,
  index,
  fieldsToDisplay,
  updateFields,
  minMovableIndex
}) => {
  const [sequence, setSequence] = React.useState(null);
  const [fieldDropdownValue, setFieldDropdownValue] = React.useState(null);
  const { Alert, setMessage } = useAlert({});
  const moveField = dndMove(fieldsToDisplay, updateFields);
  const handleMove = () => {
    if (!sequence && (!fieldDropdownValue || fieldDropdownValue == 'select')) {
      setMessage('Please select a sequence and field option');
    } else if (!sequence) {
      setMessage('Please select a sequence option');
    } else if (!fieldDropdownValue || fieldDropdownValue == 'select') {
      setMessage('Please select a field option');
    } else {
      const targetIndex =
        sequence == 'above' ? fieldDropdownValue - 1 : fieldDropdownValue;
      const adjustedIndex = Math.max(targetIndex, minMovableIndex);
      moveField(index, adjustedIndex);
      setShowMoveModal(false);
    }
  };
  const fieldsToDisplayForDropdown = fieldsToDisplay.filter(
    f =>
      !NON_MOVABLE_FIELDS.includes(f.field) &&
      f.field !== fieldsToDisplay[index].field
  );
  return (
    <div className="move-modal-wrapper">
      <Modal
        id="moveModal"
        header="Move To"
        onClose={() => setShowMoveModal(false)}
        footer={
          <Buttons>
            <Cancel onClick={() => setShowMoveModal(false)}></Cancel>
            <Primary label="Move" onClick={() => handleMove()}></Primary>
          </Buttons>
        }
      >
        <Alert />
        <label>Select Sequence</label>
        <div className="move-radio-group">
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="sequence"
              value="above"
              onChange={e => setSequence(e.target.value)}
            ></input>
            <label className="form-check-label">Above</label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="sequence"
              value="below"
              onChange={e => setSequence(e.target.value)}
            ></input>
            <label className="form-check-label">Below</label>
          </div>
        </div>
        <label>Select Field</label>
        <div className="move-dropdown-group">
          <select
            className="form-control"
            onChange={e => setFieldDropdownValue(e.target.selectedIndex)}
          >
            <option value="select">Select...</option>
            {fieldsToDisplayForDropdown.map(({ field, name }, currIndex) => {
              if (currIndex != index) {
                return (
                  <option key={field} value={field}>
                    {name}
                  </option>
                );
              }
              return null;
            })}
          </select>
        </div>
      </Modal>
    </div>
  );
};
